import { Button, type ButtonSize } from './Button';
import { cx } from '@/lib/cx';

/**
 * One choice in a {@link SegmentedControl}.
 *
 * `label` is the visible word AND the accessible name, deliberately: a
 * segmented control exists so both are the same thing. There is no `hint` and
 * there must not be one — this renders inside a desktop grid cell, where
 * nothing explanatory may appear.
 */
export interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
  /**
   * Optional `title` on the segment. Reserved for a value the label truncates,
   * never for advice — see the no-helper-text rule.
   */
  title?: string;
}

export interface SegmentedControlProps<T extends string> {
  /** Names the CHOICE, not the current answer — it becomes the group's label. */
  label: string;
  value: T;
  options: readonly SegmentedControlOption<T>[];
  onChange: (value: T) => void;
  size?: ButtonSize;
  disabled?: boolean;
  className?: string;
}

/**
 * A two-or-more-way choice rendered as one joined control.
 *
 * ── WHY THIS IS A PRIMITIVE AND NOT A PAIR OF BUTTONS AT THE CALL SITE ──────
 *
 * The three line-item grids each carried their own "which unit is this box in"
 * switch, and each drew it differently — one as an icon-only button inside the
 * input's trailing slot, one as two loose `sm` buttons under it. The result was
 * that the SAME choice read as a different kind of thing on three screens, and
 * on two of them it did not read as a choice at all. The house rule is to fix
 * the primitive rather than to work around it at the call site, so the shape
 * lives here once.
 *
 * ── THE THREE THINGS THAT ARE NOT DECORATION ────────────────────────────────
 *
 * 1. **`role="group"` + `aria-pressed` per segment**, not a `radiogroup`. Every
 *    segment stays an ordinary `<button>`, so it is in the tab order, takes
 *    Enter and Space, and is announced with the keys it really accepts. A
 *    `radiogroup` would promise arrow-key roving that these buttons do not
 *    implement, and an announcement that does not match the behaviour is worse
 *    than a plainer one that does.
 *
 * 2. ⚠️ **The selected segment is NOT distinguished by colour alone.** It is a
 *    raised tab — its own border and the surface ground — sitting inside the
 *    sunken track, where the other segments have neither. That is a SHAPE
 *    signal, and it is the native segmented-control idiom. ⚠️ *SUPERSEDED
 *    2026-10-11: an accent-filled segment with a tick and a heavier weight.*
 *    The accent is reserved for primary actions, the active nav row and focus;
 *    a violet segment with a check mark is the framework-demo look. No weight
 *    change either, so the labels do not shift as the selection moves.
 *
 * 3. **Re-selecting the current option fires NOTHING.** `onChange` is a change,
 *    so a caller may treat every call as one — in the grids it marks a row
 *    dirty, and a no-op click that says "unsaved" is a false statement about
 *    the row.
 *
 * ⚠️ **Segments size to their CONTENT, with `flex-1` only distributing the
 * SLACK.** An equal split would clip the longer word inside a fixed-width table
 * cell; here the natural widths are the floor, so a cell too narrow overflows
 * visibly rather than ellipsing a label silently. Give the cell room for the
 * longest label — the width is deterministic, because the labels are constants.
 */
export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
  size = 'sm',
  disabled = false,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cx(
        'inline-flex w-full items-stretch gap-0.5 rounded-md border border-border bg-surface-sunken p-0.5',
        className,
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Button
            key={option.value}
            type="button"
            size={size}
            variant={selected ? 'secondary' : 'ghost'}
            aria-pressed={selected}
            disabled={disabled}
            {...(option.title ? { title: option.title } : {})}
            className="flex-1"
            onClick={() => {
              if (!selected) onChange(option.value);
            }}
          >
            <span className="whitespace-nowrap">{option.label}</span>
          </Button>
        );
      })}
    </div>
  );
}
